import { AUTH_CONFIG, AuthConfig, DEFAULT_SESSION_CONFIG } from './auth-config-token';
import { fromEvent, merge, Subject, BehaviorSubject, Observable } from 'rxjs';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class ActivityMonitorService implements OnDestroy { // Not used currently

    private readonly destroy$: Subject<void> = new Subject<void>();

    private readonly config: AuthConfig = inject(AUTH_CONFIG);

    private readonly document: Document = inject(DOCUMENT);

    private readonly _lastActivity = new BehaviorSubject<Date>(new Date());

    public readonly lastActivity$ = this._lastActivity.asObservable();

    private readonly _isActive = new BehaviorSubject<boolean>(true);

    public readonly isActive$ = this._isActive.asObservable();

    private activityTimer?: number;

    private readonly sessionConfig = {
        ...DEFAULT_SESSION_CONFIG,
        ...(this.config.sessionConfig || {})
    };

    constructor() {
        if (this.sessionConfig.enableActivityMonitoring) {
            this.initializeActivityMonitoring();
        }
    }

    private initializeActivityMonitoring(): void {
        // Create observables for user activity events
        const mouseMove$: Observable<MouseEvent> = fromEvent<MouseEvent>(this.document, 'mousemove');
        
        const mouseClick$: Observable<MouseEvent> = fromEvent<MouseEvent>(this.document, 'click');
        
        const keyPress$: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(this.document, 'keydown');
        
        const scroll$: Observable<Event> = fromEvent<Event>(this.document, 'scroll');
        
        const touchStart$: Observable<TouchEvent> = fromEvent<TouchEvent>(this.document, 'touchstart');
        
        // Merge all activity streams
        merge(mouseMove$, mouseClick$, keyPress$, scroll$, touchStart$)
            .pipe(
                debounceTime(this.sessionConfig.activityDebounce),
                tap(() => this.updateActivity()),
                takeUntil(this.destroy$)
            )
            .subscribe();

        // Check for inactivity periodically
        this.startInactivityCheck();
    }

    private updateActivity(): void {
        this._lastActivity.next(new Date());

        this._isActive.next(true);

        // Reset inactivity timer
        if (this.activityTimer) {
            window.clearTimeout(this.activityTimer);
        }

        // Set new inactivity timer (consider inactive after heartbeat interval)
        this.activityTimer = window.setTimeout(() => {
            this._isActive.next(false);
        }, this.sessionConfig.heartbeatInterval * 60 * 1000);
    }

    private startInactivityCheck(): void {
        // Check every minute if user has been inactive
        setInterval(() => {
            const lastActivity = this._lastActivity.value;
            const now = new Date();
            const minutesSinceActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60);

            if (minutesSinceActivity > this.sessionConfig.heartbeatInterval) {
                this._isActive.next(false);
            }
        }, 60000); // Check every minute
    }

    public getMinutesSinceLastActivity(): number {
        const now = new Date();
        const lastActivity = this._lastActivity.value;
        return (now.getTime() - lastActivity.getTime()) / (1000 * 60);
    }

    public resetActivity(): void {
        this.updateActivity();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        if (this.activityTimer) {
            window.clearTimeout(this.activityTimer);
        }
    }
}